import {
    Accordion,
    ActionIcon,
    Alert,
    Badge,
    Button,
    Card,
    Center,
    Group,
    Loader,
    Select,
    Stack,
    Text,
    Textarea,
    TextInput,
    Title,
    Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { CityAttractionRO } from '@routellers/types/dist/city-attraction/city-attraction.ro';
import { AttractionReviewStatusEnum } from '@routellers/types/dist/city-attraction/enum/attraction-review-status.enum';
import { ReadyRouteStatusEnum } from '@routellers/types/dist/city-ready-route/enum/ready-route-status.enum';
import { PartnerCityReadyRouteStopDto } from '@routellers/types/dist/partners-portal/partner-city-ready-route.dto';
import { PortalCityOptionRO } from '@routellers/types/dist/partners-portal/portal-city-option.ro';
import { Languages } from '@routellers/types/dist/shared/enum/languages.enum';
import { RoutePoint } from '@routellers/utils/dist';
import {
    IconArrowLeft,
    IconInfoCircle,
    IconMapPin,
    IconPlus,
    IconSend,
    IconTrash,
} from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { api } from '../api';
import { apiErrorMessage } from '../api/client';
import { AttractionFormModal } from '../components/attraction-form-modal';
import { AttractionPicker } from '../components/attraction-picker';
import { RouteMap } from '../components/route-map';
import { RouteWarnings } from '../components/route-warnings';
import { RouteStatusBadge } from '../components/status-badge';
import { t } from '../dictionary';
import { fameLabel } from '../utils/attraction-labels';

const languageLabel = (code: string) => {
    try {
        return new Intl.DisplayNames(['en'], { type: 'language' }).of(code) ?? code;
    } catch {
        return code;
    }
};

const languageOptions = Object.values(Languages).map((code) => ({
    value: code,
    label: languageLabel(code),
}));

type EditorStep = {
    id?: number;
    title: string;
    attractionId: number;
    hook: string;
    guideTalk: string;
};

const emptyStep = (): EditorStep => ({
    title: '',
    attractionId: 0,
    hook: '',
    guideTalk: '',
});

const stopToEditor = (stop: PartnerCityReadyRouteStopDto): EditorStep => ({
    id: stop.id,
    title: stop.title || '',
    attractionId: stop.attractionId || 0,
    hook: stop.hook || '',
    guideTalk: stop.guideTalk || '',
});

const editorToStop = (step: EditorStep, order: number): PartnerCityReadyRouteStopDto => ({
    id: step.id,
    order,
    attractionId: step.attractionId,
    title: step.title.trim(),
    hook: step.hook.trim() || undefined,
    guideTalk: step.guideTalk.trim() || undefined,
});

const EDITABLE = [ReadyRouteStatusEnum.DRAFT, ReadyRouteStatusEnum.REJECTED];

const stepHasText = (step: EditorStep) =>
    [step.title, step.hook, step.guideTalk].some((v) => !!v?.trim());

export const RouteEditorPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = !id;

    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [status, setStatus] = useState<ReadyRouteStatusEnum>(ReadyRouteStatusEnum.DRAFT);
    const [rejectionReason, setRejectionReason] = useState<string | null>(null);

    const [title, setTitle] = useState('');
    const [teaser, setTeaser] = useState('');
    const [description, setDescription] = useState('');
    const [cityId, setCityId] = useState<number | null>(null);
    const [cityTitle, setCityTitle] = useState<string | null>(null);
    const [citySearch, setCitySearch] = useState('');
    const [cities, setCities] = useState<PortalCityOptionRO[]>([]);
    const [language, setLanguage] = useState<string>(Languages.EN);
    const [steps, setSteps] = useState<EditorStep[]>([emptyStep()]);
    const [attractionsById, setAttractionsById] = useState<Record<number, CityAttractionRO>>({});

    const [pickerStepIndex, setPickerStepIndex] = useState<number | null>(null);
    const [createOpen, setCreateOpen] = useState(false);

    const cityLocked = steps.some((s) => s.attractionId > 0);
    const languageLocked = steps.some(stepHasText);
    const readOnly = !isNew && !EDITABLE.includes(status);

    useEffect(() => {
        const timer = setTimeout(() => {
            api.questCities(citySearch)
                .then(({ data }) => setCities(data))
                .catch(() => setCities([]));
        }, 250);

        return () => clearTimeout(timer);
    }, [citySearch]);

    useEffect(() => {
        if (isNew) return;

        api.route(+id)
            .then(async ({ data }) => {
                setTitle(data.title || '');
                setTeaser(data.teaser || '');
                setDescription(data.description || '');
                setCityId(data.cityId ?? null);
                setCityTitle(data.cityTitle ?? null);
                setLanguage(data.language || Languages.EN);
                setSteps(data.stops?.length ? data.stops.map(stopToEditor) : [emptyStep()]);
                setStatus(data.status);
                setRejectionReason(data.rejectionReason ?? null);

                if (data.cityId) {
                    try {
                        const { data: list } = await api.attractions({ cityId: data.cityId });
                        const map: Record<number, CityAttractionRO> = {};

                        for (const a of list) map[a.id] = a;
                        setAttractionsById(map);
                    } catch {
                        setAttractionsById({});
                    }
                }
            })
            .catch(() => navigate('/routes'))
            .finally(() => setLoading(false));
    }, [id, isNew, navigate]);

    const cityOptions = useMemo(() => {
        const options = cities.map((c) => ({
            value: String(c.id),
            label: `${c.title} (${c.country?.toUpperCase?.() || ''})`,
        }));

        if (cityId && cityTitle && !options.some((o) => o.value === String(cityId))) {
            options.unshift({ value: String(cityId), label: cityTitle });
        }

        return options;
    }, [cities, cityId, cityTitle]);

    const patchStep = (index: number, patch: Partial<EditorStep>) => {
        setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
    };

    const rememberAttraction = (attraction: CityAttractionRO) => {
        setAttractionsById((prev) => ({ ...prev, [attraction.id]: attraction }));
    };

    const assignAttraction = (index: number, attraction: CityAttractionRO) => {
        rememberAttraction(attraction);
        setSteps((prev) =>
            prev.map((s, i) =>
                i === index
                    ? {
                          ...s,
                          attractionId: attraction.id,
                          title: attraction.title,
                      }
                    : s,
            ),
        );
    };

    const routePoints: RoutePoint[] = useMemo(() => {
        const points: RoutePoint[] = [];

        for (const step of steps) {
            if (!step.attractionId) continue;
            const a = attractionsById[step.attractionId];

            if (a?.lat != null && a?.lng != null) {
                points.push({ lat: a.lat, lng: a.lng });
            }
        }

        return points;
    }, [steps, attractionsById]);

    const mapPoints = useMemo(
        () =>
            steps
                .map((step) => {
                    const a = step.attractionId ? attractionsById[step.attractionId] : undefined;

                    if (!a || a.lat == null || a.lng == null) return null;

                    return { lat: a.lat, lng: a.lng, label: step.title || a.title };
                })
                .filter(Boolean) as { lat: number; lng: number; label?: string }[],
        [steps, attractionsById],
    );

    const hasUnapproved = steps.some((s) => {
        if (!s.attractionId) return false;
        const a = attractionsById[s.attractionId];

        return !a || a.reviewStatus !== AttractionReviewStatusEnum.APPROVED;
    });

    const save = async (opts?: { forSubmit?: boolean }): Promise<number | null> => {
        if (!title.trim()) {
            notifications.show({ color: 'red', message: t.routes.titleRequired });

            return null;
        }

        if (!cityId) {
            notifications.show({ color: 'red', message: t.routes.cityRequired });

            return null;
        }

        const payloadSteps = steps
            .map((s, i) => ({ step: s, order: i + 1 }))
            .filter(({ step }) => step.title.trim() && step.attractionId > 0)
            .map(({ step, order }) => editorToStop(step, order));

        if (opts?.forSubmit && payloadSteps.length < 2) {
            notifications.show({ color: 'red', message: t.routes.needStops });

            return null;
        }

        if (opts?.forSubmit && payloadSteps.some((s) => !s.guideTalk?.trim())) {
            notifications.show({ color: 'yellow', message: t.routes.needGuideTalk });
        }

        setSaving(true);

        try {
            const payload = {
                title,
                teaser: teaser.trim() || null,
                description: description.trim() || null,
                cityId: cityId!,
                language: language as Languages,
                stops: payloadSteps,
            };

            if (isNew) {
                const { data } = await api.createRoute(payload);

                return +data.id;
            }

            await api.updateRoute(+id, payload);

            return +id;
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });

            return null;
        } finally {
            setSaving(false);
        }
    };

    const saveDraft = async () => {
        const savedId = await save();

        if (!savedId) return;

        if (isNew) {
            navigate(`/routes/${savedId}`, { replace: true });

            return;
        }

        notifications.show({ color: 'green', message: t.common.saved });
    };

    const submitForReview = async () => {
        if (hasUnapproved) {
            notifications.show({ color: 'red', message: t.routes.unapprovedBlockSubmit });

            return;
        }

        const savedId = await save({ forSubmit: true });

        if (!savedId) return;

        setSubmitting(true);

        try {
            await api.submitRoute(savedId);
            notifications.show({ color: 'green', message: t.routes.submitted });
            navigate('/routes');
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSubmitting(false);
        }
    };

    const deleteRoute = async () => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(t.routes.deleteConfirm)) return;

        try {
            await api.deleteRoute(+id);
            navigate('/routes');
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        }
    };

    if (loading) {
        return (
            <Center h={300}>
                <Loader />
            </Center>
        );
    }

    const selectedCount = steps.filter((s) => s.attractionId > 0).length;

    return (
        <>
            <Group justify='space-between' mb='lg' wrap='wrap'>
                <Group gap='sm'>
                    <ActionIcon component={Link} to='/routes' variant='subtle' color='gray'>
                        <IconArrowLeft size={20} />
                    </ActionIcon>
                    <Title order={2}>
                        {isNew ? t.routes.editorTitleNew : t.routes.editorTitleEdit}
                    </Title>
                    {!isNew && <RouteStatusBadge status={status} />}
                </Group>

                {!readOnly && (
                    <Group gap='sm'>
                        {!isNew && (
                            <Tooltip label={t.common.delete}>
                                <ActionIcon variant='subtle' color='red' onClick={deleteRoute}>
                                    <IconTrash size={18} />
                                </ActionIcon>
                            </Tooltip>
                        )}
                        <Button variant='default' loading={saving} onClick={saveDraft}>
                            {t.routes.saveDraft}
                        </Button>
                        <Button
                            leftSection={<IconSend size={16} />}
                            loading={submitting}
                            disabled={hasUnapproved}
                            onClick={submitForReview}
                        >
                            {t.routes.submit}
                        </Button>
                    </Group>
                )}
            </Group>

            <Stack gap='lg'>
                {readOnly && (
                    <Alert variant='light' color='indigo' icon={<IconInfoCircle />}>
                        {t.routes.readOnlyNotice}
                    </Alert>
                )}

                {status === ReadyRouteStatusEnum.REJECTED && rejectionReason && (
                    <Alert variant='light' color='red' title={t.routes.rejectionTitle}>
                        {rejectionReason}
                    </Alert>
                )}

                {!readOnly && (
                    <Accordion variant='contained' radius='md'>
                        <Accordion.Item value='guide'>
                            <Accordion.Control>
                                <Text fw={650}>{t.routes.guideTitle}</Text>
                            </Accordion.Control>
                            <Accordion.Panel>
                                <Stack gap='sm'>
                                    <Text size='sm' c='dimmed'>
                                        {t.routes.guideIntro}
                                    </Text>
                                    <Stack gap={6}>
                                        {t.routes.guideBullets.map((line) => (
                                            <Text key={line} size='sm'>
                                                • {line}
                                            </Text>
                                        ))}
                                    </Stack>
                                </Stack>
                            </Accordion.Panel>
                        </Accordion.Item>
                    </Accordion>
                )}

                <Card padding='lg'>
                    <Stack gap='md'>
                        <TextInput
                            label={t.routes.routeTitle}
                            description={t.routes.routeTitleHint}
                            placeholder={t.routes.routeTitlePlaceholder}
                            value={title}
                            disabled={readOnly}
                            onChange={(e) => setTitle(e.currentTarget.value)}
                        />

                        <Textarea
                            label={t.routes.teaser}
                            description={t.routes.teaserHint}
                            placeholder={t.routes.teaserPlaceholder}
                            minRows={2}
                            autosize
                            value={teaser}
                            disabled={readOnly}
                            onChange={(e) => setTeaser(e.currentTarget.value)}
                        />

                        <Textarea
                            label={t.routes.description}
                            description={t.routes.descriptionHint}
                            placeholder={t.routes.descriptionPlaceholder}
                            minRows={3}
                            autosize
                            value={description}
                            disabled={readOnly}
                            onChange={(e) => setDescription(e.currentTarget.value)}
                        />

                        <Group grow align='flex-start'>
                            <Select
                                label={t.routes.citySelect}
                                placeholder={t.routes.citySelectPlaceholder}
                                data={cityOptions}
                                value={cityId ? String(cityId) : null}
                                disabled={readOnly || cityLocked}
                                searchable
                                searchValue={citySearch}
                                onSearchChange={setCitySearch}
                                onChange={(v) => {
                                    const nextId = v ? +v : null;
                                    const city = cities.find((c) => c.id === nextId);

                                    setCityId(nextId);
                                    setCityTitle(city?.title ?? null);
                                    setAttractionsById({});
                                }}
                            />
                            <Select
                                label={t.routes.language}
                                description={t.routes.languageHint}
                                data={languageOptions}
                                value={language}
                                disabled={readOnly || languageLocked}
                                onChange={(v) => v && setLanguage(v)}
                                searchable
                            />
                        </Group>

                        {cityLocked && !readOnly && (
                            <Alert variant='light' color='gray' icon={<IconInfoCircle />}>
                                {t.routes.cityLockedNotice}
                            </Alert>
                        )}
                    </Stack>
                </Card>

                {!cityId ? (
                    <Alert variant='light' color='indigo' icon={<IconMapPin />}>
                        {t.routes.cityRequired}
                    </Alert>
                ) : (
                    <>
                        <Card padding='lg'>
                            <Text fw={650}>{t.routes.stepsTitle}</Text>
                            <Text size='sm' c='dimmed' mb='md'>
                                {t.routes.stepsSubtitle}
                            </Text>

                            <Accordion variant='separated' radius='md' defaultValue='step-0'>
                                {steps.map((step, index) => {
                                    const attraction = step.attractionId
                                        ? attractionsById[step.attractionId]
                                        : undefined;

                                    return (
                                        <Accordion.Item key={index} value={`step-${index}`}>
                                            <Accordion.Control>
                                                <Group gap='sm' wrap='nowrap'>
                                                    <Badge circle size='sm' variant='light'>
                                                        {index + 1}
                                                    </Badge>
                                                    <Text fw={600} truncate>
                                                        {step.title || t.routes.untitledStop}
                                                    </Text>
                                                </Group>
                                            </Accordion.Control>
                                            <Accordion.Panel>
                                                <Stack gap='md'>
                                                    <Group justify='space-between'>
                                                        <div>
                                                            {attraction ? (
                                                                <Stack gap={2}>
                                                                    <Text fw={600}>
                                                                        {attraction.title}
                                                                    </Text>
                                                                    <Text size='xs' c='dimmed'>
                                                                        {[
                                                                            fameLabel(
                                                                                attraction.fame,
                                                                            ),
                                                                            attraction.district,
                                                                        ]
                                                                            .filter(Boolean)
                                                                            .join(' · ')}
                                                                    </Text>
                                                                    {attraction.reviewStatus !==
                                                                        AttractionReviewStatusEnum.APPROVED && (
                                                                        <Badge
                                                                            size='xs'
                                                                            color='yellow'
                                                                            variant='light'
                                                                            w='fit-content'
                                                                        >
                                                                            {
                                                                                t.routes
                                                                                    .attractionNeedReview
                                                                            }
                                                                        </Badge>
                                                                    )}
                                                                </Stack>
                                                            ) : (
                                                                <Text size='sm' c='dimmed'>
                                                                    {t.routes.noAttractionSelected}
                                                                </Text>
                                                            )}
                                                        </div>
                                                        {!readOnly && (
                                                            <Button
                                                                size='xs'
                                                                variant='light'
                                                                onClick={() =>
                                                                    setPickerStepIndex(index)
                                                                }
                                                            >
                                                                {attraction
                                                                    ? t.routes.changeAttraction
                                                                    : t.routes.pickAttraction}
                                                            </Button>
                                                        )}
                                                    </Group>

                                                    <TextInput
                                                        label={t.routes.stepTitle}
                                                        placeholder={t.routes.stepTitlePlaceholder}
                                                        value={step.title}
                                                        disabled={readOnly}
                                                        onChange={(e) =>
                                                            patchStep(index, {
                                                                title: e.currentTarget.value,
                                                            })
                                                        }
                                                    />

                                                    <Textarea
                                                        label={t.routes.stepHook}
                                                        description={t.routes.stepHookHint}
                                                        placeholder={t.routes.stepHookPlaceholder}
                                                        minRows={2}
                                                        autosize
                                                        value={step.hook}
                                                        disabled={readOnly}
                                                        onChange={(e) =>
                                                            patchStep(index, {
                                                                hook: e.currentTarget.value,
                                                            })
                                                        }
                                                    />

                                                    <Textarea
                                                        label={t.routes.stepGuideTalk}
                                                        description={t.routes.stepGuideTalkHint}
                                                        placeholder={
                                                            t.routes.stepGuideTalkPlaceholder
                                                        }
                                                        minRows={4}
                                                        autosize
                                                        value={step.guideTalk}
                                                        disabled={readOnly}
                                                        onChange={(e) =>
                                                            patchStep(index, {
                                                                guideTalk: e.currentTarget.value,
                                                            })
                                                        }
                                                    />

                                                    {!readOnly && steps.length > 1 && (
                                                        <Button
                                                            variant='subtle'
                                                            color='red'
                                                            size='xs'
                                                            leftSection={<IconTrash size={14} />}
                                                            onClick={() =>
                                                                setSteps((prev) =>
                                                                    prev.filter(
                                                                        (_, i) => i !== index,
                                                                    ),
                                                                )
                                                            }
                                                        >
                                                            {t.routes.removeStep}
                                                        </Button>
                                                    )}
                                                </Stack>
                                            </Accordion.Panel>
                                        </Accordion.Item>
                                    );
                                })}
                            </Accordion>

                            {!readOnly && (
                                <Button
                                    mt='md'
                                    variant='light'
                                    leftSection={<IconPlus size={16} />}
                                    onClick={() => setSteps((prev) => [...prev, emptyStep()])}
                                >
                                    {t.routes.addStep}
                                </Button>
                            )}
                        </Card>

                        {selectedCount > 0 && (
                            <Card padding='lg'>
                                <Text fw={650} mb='sm'>
                                    {t.routes.routePreview}
                                </Text>
                                <Stack gap='md'>
                                    <RouteMap points={mapPoints} />
                                    <RouteWarnings
                                        points={routePoints}
                                        stepCount={selectedCount}
                                        hasUnapproved={hasUnapproved}
                                    />
                                </Stack>
                            </Card>
                        )}
                    </>
                )}
            </Stack>

            {cityId != null && (
                <>
                    <AttractionPicker
                        opened={pickerStepIndex != null && !createOpen}
                        onClose={() => setPickerStepIndex(null)}
                        cityId={cityId}
                        onSelect={(attraction) => {
                            if (pickerStepIndex == null) return;
                            assignAttraction(pickerStepIndex, attraction);
                        }}
                        onCreateNew={() => setCreateOpen(true)}
                    />
                    <AttractionFormModal
                        opened={createOpen}
                        onClose={() => setCreateOpen(false)}
                        cityId={cityId}
                        onCreated={(attraction) => {
                            if (pickerStepIndex == null) return;
                            assignAttraction(pickerStepIndex, attraction);
                            setPickerStepIndex(null);
                        }}
                    />
                </>
            )}
        </>
    );
};
